/**
 * Cadastro e edição de equipamento de incêndio (#363, D8 e D11): uma tela só, em três grupos
 * (Qual é o equipamento · Dados do equipamento · Validades e inspeção), sem etapas, com
 * "Salvar e cadastrar outro" para quem cadastra dezenas de extintores iguais. Os campos de cada
 * tipo e os rótulos vêm de `GET /fire-equipments/types`; as mensagens de erro são as do servidor.
 *
 * Alvo visual: docs/superpowers/specs/2026-10-15-363-incendio-nr23-mockup.html (aba 2).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Alert, Box, Button, Card, CardContent, Skeleton, TextField, Typography } from '@mui/material';

import { PageShell, RouteActionButton } from '../../components';
import { pode } from '../../authorization/pode';
import { useListaPermitida } from '../../authorization/useListaPermitida';
import { useCompanyContext } from '../../hooks/useCompanyContext';
import { companyService } from '../../services/companyService';
import { departmentService } from '../../services/departmentService';
import { fireEquipmentService } from '../../services/fireEquipmentService';
import { ApiRequestError } from '../../types/common';
import type {
  FireEquipmentDetail,
  FireEquipmentType,
  FireEquipmentTypeDescriptor,
  FireEquipmentTypes,
} from '../../types/fireEquipment';
import { TypeFields } from './components/TypeFields';
import { HELPER_TEXT_PROPS, LABEL_ABOVE } from './components/formFieldProps';
import {
  EMPTY_VALUES,
  buildPayload,
  valuesForNext,
  valuesFromEquipment,
  validateField,
  validateForm,
  type FireFormErrors,
  type FireFormField,
  type FireFormValues,
} from './fireEquipmentForm';

const TOUCH_TARGET = 44;
/**
 * Início da mensagem do servidor para identificação repetida. O servidor devolve esse caso como
 * um `BUSINESS_RULE_ERROR` genérico, sem campo estruturado (`details`) que o distinga de outras
 * regras de negócio; enquanto isso não existir, só o texto o reconhece.
 */
const IDENTIFIER_TAKEN_PREFIX = 'Já existe um equipamento';

const errorMessage = (err: unknown, fallback: string): string => (err instanceof Error ? err.message : fallback);

const isNotFound = (err: unknown): boolean => err instanceof ApiRequestError && err.status === 404;

/** O tipo escolhido: leva a frequência padrão dele e tira o que o tipo novo não usa. */
function withType(values: FireFormValues, descriptor: FireEquipmentTypeDescriptor): FireFormValues {
  const uses = (name: string) => descriptor.fields.some((field) => field.name === name);
  return {
    ...values,
    type: descriptor.type,
    inspectionFrequency: descriptor.defaultFrequency,
    extinguishingAgent: uses('extinguishingAgent') ? values.extinguishingAgent : '',
    capacityValue: uses('capacityValue') ? values.capacityValue : '',
    capacityUnit: uses('capacityUnit') ? values.capacityUnit || 'KG' : '',
    chargeExpiryDate: uses('chargeExpiryDate') ? values.chargeExpiryDate : '',
    hydrostaticExpiryDate: uses('hydrostaticExpiryDate') ? values.hydrostaticExpiryDate : '',
    description: uses('description') ? values.description : '',
  };
}

const FormGroup: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Card variant="outlined" sx={{ mb: 3 }}>
    <CardContent>
      <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
        {title}
      </Typography>
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>{children}</Box>
    </CardContent>
  </Card>
);

export const FireEquipmentFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const editing = Boolean(id);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { shouldShowCompanyField } = useCompanyContext();
  const asksCompany = shouldShowCompanyField && !editing;

  const [tipos, setTipos] = useState<FireEquipmentTypes | null>(null);
  const [equipment, setEquipment] = useState<FireEquipmentDetail | null>(null);
  const [values, setValues] = useState<FireFormValues>(EMPTY_VALUES);
  const [errors, setErrors] = useState<FireFormErrors>({});
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [focusToken, setFocusToken] = useState(0);
  const identifierRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let alive = true;
    setLoadError(null);
    setNotFound(false);
    setTipos(null);

    Promise.all([fireEquipmentService.getTypes(), id ? fireEquipmentService.getFireEquipmentById(id) : null])
      .then(([types, loaded]) => {
        if (!alive) return;
        setTipos(types);
        if (loaded) {
          setEquipment(loaded);
          setValues(valuesFromEquipment(loaded));
        } else if (types.types.length > 0) {
          setValues((current) => withType(current, types.types[0]));
        }
      })
      .catch((err: unknown) => {
        if (!alive) return;
        if (isNotFound(err)) setNotFound(true);
        else setLoadError(errorMessage(err, 'Erro ao carregar o formulário'));
      });

    return () => {
      alive = false;
    };
  }, [id, attempt]);

  useEffect(() => {
    if (focusToken > 0) identifierRef.current?.focus();
  }, [focusToken]);

  const descriptor = useMemo(
    () => tipos?.types.find((candidate) => candidate.type === values.type),
    [tipos, values.type]
  );
  const options = useMemo(() => ({ requireCompany: asksCompany }), [asksCompany]);

  const companyIdForDepartments = values.companyId;
  const loadCompanyOptions = useCallback(async (): Promise<readonly { value: string; label: string }[]> => {
    const companies = await companyService.getCompaniesForDropdown();
    return companies.map((company) => ({ value: String(company.id), label: company.name }));
  }, []);
  const companyList = useListaPermitida('company.read', 'Empresas', loadCompanyOptions, { habilitado: asksCompany });

  const loadDepartmentOptions = useCallback(async (): Promise<readonly { value: string; label: string }[]> => {
    const response = await departmentService.getDepartments({
      page: 1,
      limit: 1000,
      ...(companyIdForDepartments ? { companyId: companyIdForDepartments } : {}),
    });
    return response.data.map((department) => ({ value: String(department.id), label: department.name }));
  }, [companyIdForDepartments]);
  const departmentList = useListaPermitida('department.read', 'Setores', loadDepartmentOptions, {
    habilitado: tipos !== null && (!asksCompany || companyIdForDepartments !== ''),
  });

  // Habilitada e ainda sem resposta: o select estaria vazio por não se saber, não por não haver.
  const companiesLoading = companyList.permitido && !companyList.resolvida;
  const departmentsLoading = departmentList.permitido && !departmentList.resolvida;

  const decommissioned = equipment?.status === 'DECOMMISSIONED';
  const reactivating = decommissioned && values.status === 'IN_USE';
  const scheduleLocked = decommissioned && !reactivating;

  const change = (field: FireFormField, value: string) => {
    const next = { ...values, [field]: value } as FireFormValues;
    if (field === 'companyId') next.departmentId = '';
    setValues(next);
    if (errors[field] && descriptor) {
      setErrors((current) => ({ ...current, [field]: validateField(field, next, descriptor, options) ?? undefined }));
    }
  };

  const blur = (field: FireFormField) => {
    setErrors((current) => ({ ...current, [field]: validateField(field, values, descriptor, options) ?? undefined }));
  };

  const changeType = (type: string) => {
    const chosen = tipos?.types.find((candidate) => candidate.type === type);
    if (!chosen) return;
    setValues((current) => withType(current, chosen));
    setErrors({});
  };

  const save = async (another: boolean) => {
    if (!descriptor) return;

    const found = validateForm(values, descriptor, options);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      setFormError('Corrija os campos marcados para salvar.');
      return;
    }

    setSubmitting(true);
    setFormError(null);
    try {
      if (editing && id) {
        const payload = buildPayload(values, descriptor, 'update', {
          includeCompany: false,
          frozenSchedule: scheduleLocked,
        });
        await fireEquipmentService.updateFireEquipment(id, payload);
        enqueueSnackbar(
          reactivating
            ? `${values.identifier.trim()} voltou a ser usado. Confira as validades.`
            : `${values.identifier.trim()} atualizado.`,
          { variant: 'success' }
        );
        navigate(`/fire-equipments/${id}`);
        return;
      }

      const created = await fireEquipmentService.createFireEquipment(
        buildPayload(values, descriptor, 'create', { includeCompany: asksCompany })
      );
      enqueueSnackbar(`${created.identifier} cadastrado.`, { variant: 'success' });
      if (another) {
        setValues(valuesForNext(values));
        setErrors({});
        setFocusToken((token) => token + 1);
      } else {
        navigate(`/fire-equipments/${created.id}`);
      }
    } catch (err: unknown) {
      const message = errorMessage(err, 'Não foi possível salvar o equipamento.');
      if (err instanceof ApiRequestError && err.code === 'BUSINESS_RULE_ERROR' && message.startsWith(IDENTIFIER_TAKEN_PREFIX)) {
        setErrors((current) => ({ ...current, identifier: message }));
        setFocusToken((token) => token + 1);
      } else {
        setFormError(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const title = editing ? 'Editar equipamento' : 'Novo equipamento';
  const breadcrumbs = [
    { label: 'Início', path: '/' },
    { label: 'Equipamentos de incêndio', path: '/fire-equipments' },
    { label: editing ? 'Editar' : 'Novo' },
  ];

  if (notFound) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title={title}>
        <Alert
          severity="info"
          action={
            <RouteActionButton
              para="/fire-equipments"
              exige={pode('fire_equipment.read')}
              color="inherit"
              sx={{ minHeight: TOUCH_TARGET }}
            >
              Voltar para a lista
            </RouteActionButton>
          }
        >
          Não encontramos este equipamento. Ele pode ter sido excluído ou ser de outra empresa.
        </Alert>
      </PageShell>
    );
  }

  if (loadError) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title={title}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" onClick={() => setAttempt((count) => count + 1)} sx={{ minHeight: TOUCH_TARGET }}>
              Tentar de novo
            </Button>
          }
        >
          {loadError}
        </Alert>
      </PageShell>
    );
  }

  if (tipos && !descriptor) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title={title}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" onClick={() => setAttempt((count) => count + 1)} sx={{ minHeight: TOUCH_TARGET }}>
              Tentar de novo
            </Button>
          }
        >
          Não encontramos os tipos de equipamento para montar o formulário.
        </Alert>
      </PageShell>
    );
  }

  if (!tipos || !descriptor) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title={title}>
        <Skeleton variant="rounded" height={160} sx={{ mb: 3 }} />
        <Skeleton variant="rounded" height={220} sx={{ mb: 3 }} />
        <Skeleton variant="rounded" height={160} />
      </PageShell>
    );
  }

  const backTo = editing && id ? `/fire-equipments/${id}` : '/fire-equipments';
  const fieldBase = (field: FireFormField, label: string, required = false) => ({
    label,
    required,
    value: values[field],
    error: Boolean(errors[field]),
    InputLabelProps: LABEL_ABOVE,
    FormHelperTextProps: HELPER_TEXT_PROPS,
    onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => change(field, event.target.value),
    onBlur: () => blur(field),
  });
  const nativeSelect = { select: true, SelectProps: { native: true } } as const;

  return (
    <PageShell
      breadcrumbs={breadcrumbs}
      title={title}
      subtitle={editing ? `${equipment?.identifier} · ${equipment?.companyName}` : undefined}
    >
      {decommissioned && !reactivating && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Este equipamento está fora de uso. Só os dados descritivos podem ser mudados; para mudar a frequência ou as
          validades, volte a usá-lo em &quot;Situação do equipamento&quot;.
        </Alert>
      )}
      {reactivating && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Ao voltar a usar o equipamento, a próxima inspeção passa a ser hoje. Confira as validades antes de salvar.
        </Alert>
      )}
      {formError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setFormError(null)}>
          {formError}
        </Alert>
      )}

      <Box
        component="form"
        noValidate
        onSubmit={(event: React.FormEvent) => {
          event.preventDefault();
          void save(false);
        }}
      >
        <FormGroup title="Qual é o equipamento">
          {asksCompany && (
            <TextField
              {...fieldBase('companyId', 'Empresa', true)}
              {...nativeSelect}
              disabled={!companyList.permitido || companiesLoading}
              helperText={errors.companyId ?? companyList.bloqueio ?? (companiesLoading ? 'Carregando empresas…' : undefined)}
            >
              <option value="">Selecione…</option>
              {companyList.itens.map((company) => (
                <option key={company.value} value={company.value}>
                  {company.label}
                </option>
              ))}
            </TextField>
          )}
          <TextField
            label="Tipo"
            required
            {...nativeSelect}
            value={values.type}
            disabled={editing}
            InputLabelProps={LABEL_ABOVE}
            FormHelperTextProps={HELPER_TEXT_PROPS}
            helperText={editing ? 'O tipo não muda depois de cadastrado. Errou? Exclua e cadastre de novo.' : undefined}
            onChange={(event) => changeType(event.target.value as FireEquipmentType)}
          >
            {tipos.types.map((type) => (
              <option key={type.type} value={type.type}>
                {type.label}
              </option>
            ))}
          </TextField>
          <TextField
            {...fieldBase('identifier', 'Identificação', true)}
            inputRef={identifierRef}
            placeholder="Ex.: EXT-031"
            helperText={
              errors.identifier ?? 'Número da plaqueta ou etiqueta colada no equipamento. Não repete na mesma empresa.'
            }
          />
          <TextField
            {...fieldBase('departmentId', 'Setor')}
            {...nativeSelect}
            disabled={!departmentList.permitido || departmentsLoading || (asksCompany && values.companyId === '')}
            helperText={
              errors.departmentId ??
              departmentList.bloqueio ??
              (departmentsLoading ? 'Carregando setores…' : undefined) ??
              (asksCompany && values.companyId === '' ? 'Escolha a empresa primeiro.' : undefined)
            }
          >
            <option value="">Nenhum</option>
            {departmentList.itens.map((department) => (
              <option key={department.value} value={department.value}>
                {department.label}
              </option>
            ))}
          </TextField>
          <TextField
            {...fieldBase('location', 'Onde fica', true)}
            placeholder="Ex.: Galpão 1, entrada principal"
            helperText={errors.location}
          />
        </FormGroup>

        <FormGroup title="Dados do equipamento">
          <TypeFields
            group="data"
            descriptor={descriptor}
            labels={tipos.labels}
            values={values}
            errors={errors}
            onChange={change}
            onBlur={blur}
          />
        </FormGroup>

        <FormGroup title="Validades e inspeção">
          <TypeFields
            group="validity"
            descriptor={descriptor}
            labels={tipos.labels}
            values={values}
            errors={errors}
            onChange={change}
            onBlur={blur}
            scheduleLocked={scheduleLocked}
          />
          <TextField
            {...fieldBase('inspectionFrequency', 'Frequência da inspeção')}
            {...nativeSelect}
            disabled={scheduleLocked}
            helperText="A próxima inspeção é calculada a partir da última registrada."
          >
            {Object.entries(tipos.labels.frequency).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </TextField>
          <TextField
            {...fieldBase('status', 'Situação do equipamento')}
            {...nativeSelect}
            helperText="Equipamento fora de uso não gera aviso nem entra no documento."
          >
            {Object.entries(tipos.labels.status).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </TextField>
        </FormGroup>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
          <Button type="submit" variant="contained" disabled={submitting} sx={{ minHeight: TOUCH_TARGET }}>
            Salvar equipamento
          </Button>
          {!editing && (
            <Button
              variant="outlined"
              disabled={submitting}
              onClick={() => void save(true)}
              sx={{ minHeight: TOUCH_TARGET }}
            >
              Salvar e cadastrar outro
            </Button>
          )}
          <RouteActionButton
            para={backTo}
            exige={pode('fire_equipment.read')}
            disabled={submitting}
            sx={{ minHeight: TOUCH_TARGET }}
          >
            Cancelar
          </RouteActionButton>
        </Box>
        {!editing && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Salvar guarda o equipamento e abre a ficha dele para registrar a primeira inspeção.
          </Typography>
        )}
      </Box>
    </PageShell>
  );
};

export default FireEquipmentFormPage;
